import { LitElement, html, css, unsafeCSS, nothing } from 'lit';
import stylesTable from './catalog-item-table.scss?inline';
import stylesTile from './catalog-item-tile.scss?inline';
import { AVAILABLE_COUNT_PROP, CATALOG_VARIANT_TABLE, CATALOG_VARIANT_TILE, EXPANDED_ATTRIBUTE } from '../constants';
import { map } from 'lit/directives/map.js';
import { createRef, ref } from 'lit/directives/ref.js';
import propertyArrayConverter from '@/helpers/propertyArrayConverter.js';
import { isLaptopMaxWidth } from '@/helpers';
import { cache } from 'lit/directives/cache.js';

export default class SotbitCatalogItem extends LitElement {
    static styles = [
        css`
            ${unsafeCSS(stylesTile)}
        `,
        css`
            ${unsafeCSS(stylesTable)}
        `,
    ];

    static properties = {
        hideImg: { type: Boolean, attribute: 'hide-img' },
        isOffers: { type: Boolean, attribute: 'is-offers' },
        isToggleOffers: { type: Boolean, attribute: 'toggle-offers' },
        offersOpen: { type: Boolean, attribute: 'offers-open' },
        variant: { type: String },
        articleProp: { type: String, attribute: 'article-prop' },
        properties: {
            type: Array,
            converter: propertyArrayConverter,
        },
        tableProperties: {
            type: Array,
            converter: propertyArrayConverter,
            attribute: 'table-properties',
        },
    };

    constructor() {
        super();

        this.hideImg = false;
        this.isOffers = false;
        this.isToggleOffers = false;
        this.variant = CATALOG_VARIANT_TILE;
        this.properties = [];
        this.tableProperties = [];
        this.articleProp = '';
        this._itemTileRef = createRef();
        this._itemTilePropertyListRef = createRef();
        this._isMouseOver = false;
        this._adjustItemTilePosition = this._adjustItemTilePosition.bind(this);
        this._cleanUpItemTilePosition = this._cleanUpItemTilePosition.bind(this);
    }

    _handleItemMouseover() {
        this.setAttribute('hovered', '');
    }

    _handleItemMouseout() {
        this.removeAttribute('hovered');
    }

    _handleOpenOffers(e) {
        if (e.target.closest('[slot]') && e.target !== e.currentTarget) {
            return;
        }
        if (this.offersOpen) {
            this.removeAttribute('offers-open');
        } else {
            this.setAttribute('offers-open', '');
        }
    }

    _handleMouseover() {
        if (!this._isMouseOver) {
            this._isMouseOver = true;

            if (this.variant === CATALOG_VARIANT_TILE && !isLaptopMaxWidth()) {
                this._adjustItemTilePosition();
                this.setAttribute(EXPANDED_ATTRIBUTE, '');
            }
        }
    }

    _handleMouseout(event) {
        if (!this._isMouseOver) {
            return;
        }

        if (!event.relatedTarget || !this.contains(event.relatedTarget)) {
            this._isMouseOver = false;

            if (this.variant === CATALOG_VARIANT_TILE && !isLaptopMaxWidth()) {
                this._cleanUpItemTilePosition();
                this.removeAttribute(EXPANDED_ATTRIBUTE);
            }
        }
    }

    _adjustItemTilePosition() {
        if (!this._itemTileRef?.value || !this._itemTilePropertyListRef?.value) {
            return;
        }

        const itemNode = this._itemTileRef.value;
        const propertyListNode = this._itemTilePropertyListRef.value;

        itemNode?.style?.setProperty('--ci-property-list-height', `${propertyListNode.offsetHeight}px`);
    }

    _cleanUpItemTilePosition() {
        if (!this._itemTileRef?.value) {
            return;
        }

        this._itemTileRef.value.removeAttribute('style');
    }

    connectedCallback() {
        super.connectedCallback();

        this.addEventListener('mouseover', this._handleMouseover);
        this.addEventListener('mouseout', this._handleMouseout);

        if (isLaptopMaxWidth()) {
            this.variant = CATALOG_VARIANT_TILE;
            this.setAttribute('variant', CATALOG_VARIANT_TILE);
        }
    }

    templateTable() {
        return html` <slot></slot>
            <div class="catalog-item" @mouseover="${this._handleItemMouseover}" @mouseout="${this._handleItemMouseout}">
                ${this.isToggleOffers
                    ? html`
                          <sotbit-catalog-section-table-cell
                              left-rounded
                              variant="catalog-item"
                              settings
                              class="column"
                          >
                              <div slot="content">
                                  <div class="offers-toggle">
                                      ${this.isOffers
                                          ? html` <div @click="${this._handleOpenOffers}">
                                                ${this.isOffers
                                                    ? html`<sotbit-icon
                                                          @click="${this.isOffers ? this._handleOpenOffers : nothing}"
                                                          icon="chevron-right"
                                                          size="m"
                                                          background
                                                          variant="secondary"
                                                          pointer
                                                      ></sotbit-icon>`
                                                    : nothing}
                                            </div>`
                                          : ''}
                                  </div>
                              </div>
                          </sotbit-catalog-section-table-cell>
                      `
                    : html`
                          <sotbit-catalog-section-table-cell settings class="column">
                              <div slot="content"></div>
                          </sotbit-catalog-section-table-cell>
                      `}

                <sotbit-catalog-section-table-cell
                    ?left-rounded="${this.isToggleOffers !== true}"
                    ?left-padding-0="${this.isToggleOffers}"
                    right-border
                    variant="catalog-item"
                    product-name
                    class="column column--name"
                >
                    <div slot="content">
                        <div class="info">
                            ${this.hideImg !== true
                                ? html` <div class="img">
                                      <slot name="img"></slot>
                                  </div>`
                                : nothing}
                            <div class="name-wrapper" id="name">
                                <div class="tags">
                                    <slot name="tag"></slot>
                                </div>
                                <div class="name">
                                    <slot name="name"></slot>
                                </div>
                            </div>
                        </div>
                    </div>
                </sotbit-catalog-section-table-cell>

                ${map(
                    this.tableProperties,
                    (property) => html`
                        <sotbit-catalog-section-table-cell
                            @click="${this.isOffers ? this._handleOpenOffers : nothing}"
                            class="column"
                            variant="catalog-item"
                            part="column-property"
                        >
                            <div slot="content">
                                <slot ?data-article="${property === this.articleProp}" name="${property}"></slot>
                            </div>
                        </sotbit-catalog-section-table-cell>
                    `
                )}

                <sotbit-catalog-section-table-cell
                    left-border
                    right-rounded
                    right-sticky
                    class="column column--counter"
                    variant="catalog-item"
                >
                    <div slot="content" class="counter-wrapper">
                        <div class="actions">
                            <div class="counter">
                                <slot name="counter"></slot>
                            </div>
                            <div class="compare">
                                <slot name="compare"></slot>
                            </div>
                        </div>
                        <slot name="min-price"></slot>
                    </div>
                </sotbit-catalog-section-table-cell>
            </div>
            ${this.isOffers
                ? html`<div class="catalog-item-offers">
                      <div class="outline"></div>
                      <slot name="offers"></slot>
                  </div>`
                : ''}`;
    }

    templateTile() {
        return html` <div class="catalog-item-back">
                <slot></slot>
            </div>
            <div class="catalog-item" ${ref(this._itemTileRef)} part="catalog-item">
                <div class="catalog-item-img">
                    <div class="catalog-item-actions">
                        <slot name="compare"></slot>
                    </div>
                    <slot name="img"></slot>
                    <div class="catalog-item-labels">
                        <slot name="tag"></slot>
                    </div>
                </div>
                <div class="catalog-item-info">
                    <div part="catalog-item-prices" class="catalog-item-prices">
                        <span class="catalog-item-price">
                            <slot name="min-price"></slot>
                        </span>
                        <span class="catalog-item-price catalog-item-price--old">
                            <slot name="min-price-old"></slot>
                        </span>
                    </div>

                    <div class="catalog-item-name" part="catalog-item-name">
                        <slot name="name"></slot>
                    </div>

                    ${map(this.properties, (property) => {
                        if (property !== this.articleProp) {
                            return;
                        }

                        return html`
                            <div class="catalog-item-article" part="catalog-item-article">
                                <slot name="${property}"></slot>
                            </div>
                        `;
                    })}
                    ${map(this.properties, (property) => {
                        if (property !== AVAILABLE_COUNT_PROP) {
                            return;
                        }

                        return html`
                            <div class="catalog-item-available" part="catalog-item-available">
                                <slot name="${property}"></slot>
                            </div>
                        `;
                    })}
                </div>
                <div class="catalog-item-counter">
                    <slot name="counter"></slot>
                </div>
                <div
                    part="catalog-item-property-list"
                    class="catalog-item-property-list"
                    ${ref(this._itemTilePropertyListRef)}
                >
                    <sotbit-scroll-panel
                        class="catalog-item-property-list-scroll"
                        part="catalog-item-property-list-scroll"
                    >
                        <div class="catalog-item-property-list-scroll__content">
                            ${map(this.properties, (property) => {
                                if (property === this.articleProp || property === AVAILABLE_COUNT_PROP) {
                                    return;
                                }

                                return html` <slot name="${property}"></slot> `;
                            })}
                        </div>
                    </sotbit-scroll-panel>
                </div>
            </div>`;
    }

    render() {
        return html`${cache(this.variant === CATALOG_VARIANT_TABLE
            ? this.templateTable()
            : this.templateTile()
        )}`;
    }
}
